import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownAZ,
  BookOpen,
  BrainCircuit,
  Check,
  Clock3,
  Dumbbell,
  LogOut,
  Minus,
  PenLine,
  Play,
  Plus,
  RotateCcw,
} from "lucide-react";
import {
  DEFAULT_STUDY_PREFERENCES,
  STUDY_INITIAL_BATCH_SIZE,
  STUDY_TARGET_MAX,
  STUDY_TARGET_MIN,
  type NewWordOrder,
  type ReviewScope,
  type StudyMode,
  type StudyPreferences,
  type StudySource,
} from "@preptrack/contracts";
import { Button, EmptyState, LoadingLine, PageHeader, Tag } from "@/components/primitives";
import { CelebrationConfetti } from "@/components/celebration-confetti";
import { ShortcutHint } from "@/components/shortcut-hint";
import { WordSenses } from "@/components/word-senses";
import { WordPronunciation } from "@/components/word-pronunciation";
import { apiFetch } from "@/lib/api";
import { spellingMismatchIndexes, studyKeyboardCommand } from "@/lib/study-flow";
import type {
  AttemptResult,
  IntroductionPrompt,
  RecallPrompt,
  SessionSnapshot,
  SpellingPrompt,
  StudyAvailability,
  StudySetupResponse,
} from "@/lib/study-types";

const SOURCE_DESCRIPTIONS: Record<StudySource, string> = {
  personal_official: "你加入阅读词库且属于 2026 考纲的词",
  personal_extra: "你在阅读中记录、但不属于当前考纲的词",
  favorites: "查词和学习过程中主动收藏的词",
  kaoyan_frequency: "《考研英语词频统计版》收录的公开高频词",
};

const EMPTY_AVAILABILITY: StudyAvailability = {
  unfinishedNewCount: 0,
  newCount: 0,
  reviewCount: 0,
  mistakeReviewCount: 0,
  practiceCount: 0,
};

function formLabels(value: unknown) {
  if (!value || typeof value !== "object") return [];
  return [...new Set(Object.values(value as Record<string, unknown>).flatMap((item) =>
    typeof item === "string" ? [item] : Array.isArray(item) ? item.filter((entry): entry is string => typeof entry === "string") : []))];
}

function useStageKeyboard(
  stage: "introduction" | "recall" | "feedback" | "completion",
  onCommand: (command: "advance" | "reveal" | "forgot" | "remembered") => void,
  recallRevealed = false,
  disabled = false,
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;
      const command = studyKeyboardCommand(event, stage, recallRevealed);
      if (!command) return;
      event.preventDefault();
      onCommand(command);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [disabled, onCommand, recallRevealed, stage]);
}

function IntroductionStage({ prompt, onContinue }: { prompt: IntroductionPrompt; onContinue: () => void }) {
  const forms = formLabels(prompt.forms);
  const displayedSenses = prompt.senses;
  useStageKeyboard("introduction", (command) => { if (command === "advance") onContinue(); });
  return (
    <section className="study-prompt study-prompt--introduction" aria-labelledby="study-headword">
      <div className="study-stage-label"><BookOpen /> 第一次 · 完整认识</div>
      <header className="study-intro-head">
        <h1 id="study-headword">{prompt.headword}</h1>
        <WordPronunciation wordId={prompt.wordId} headword={prompt.headword} phoneticUk={prompt.phoneticUk} phoneticUs={prompt.phoneticUs} />
      </header>
      <div className="study-intro-body">
        <WordSenses senses={displayedSenses} empty="这个词暂时没有词典释义。" />
        {forms.length ? <div className="study-forms"><strong>常见词形</strong><div>{forms.map((form) => <Tag key={form} variant="metadata">{form}</Tag>)}</div></div> : null}
        {prompt.memberships.length ? <div className="study-memberships">{prompt.memberships.map((membership) => <Tag key={membership.code} tone={membership.code === "official_2026_en1" ? "accent" : "neutral"}>{membership.label}</Tag>)}</div> : null}
        {prompt.sourceSentence ? <blockquote>{prompt.sourceSentence}</blockquote> : null}
        {prompt.notes ? <p className="study-personal-note"><PenLine /> {prompt.notes}</p> : null}
      </div>
      <div className="study-stage-actions"><Button variant="primary" size="lg" onClick={onContinue} aria-keyshortcuts="Enter Space"><Check /> 已阅读，继续 <ShortcutHint keys={["Enter", "Space"]} /></Button></div>
    </section>
  );
}

function RecallStage({ prompt, busy, onAnswer }: { prompt: RecallPrompt; busy: boolean; onAnswer: (remembered: boolean) => void }) {
  const [revealed, setRevealed] = useState(false);
  useStageKeyboard("recall", (command) => {
    if (command === "reveal") setRevealed(true);
    if (command === "forgot") onAnswer(false);
    if (command === "remembered") onAnswer(true);
  }, revealed, busy);
  return (
    <section className="study-prompt study-prompt--recall" aria-labelledby="recall-headword">
      <div className="study-stage-label"><BrainCircuit /> 回忆词义</div>
      <h1 id="recall-headword">{prompt.headword}</h1>
      <WordPronunciation wordId={prompt.wordId} headword={prompt.headword} phoneticUk={prompt.phoneticUk} phoneticUs={prompt.phoneticUs} />
      {revealed ? (
        <div className="study-recall-answer"><WordSenses senses={prompt.senses} empty="这个词暂时没有词典释义。" /><p>对照刚才想起的含义，再选择真实结果。</p></div>
      ) : (
        <button type="button" className="study-reveal" onClick={() => setRevealed(true)} aria-keyshortcuts="Enter Space"><span>在心里回忆中文含义</span><small>准备好后显示释义 <ShortcutHint keys={["Enter", "Space"]} /></small></button>
      )}
      <div className="study-stage-actions study-stage-actions--split">
        {revealed ? <>
          <Button variant="secondary" size="lg" onClick={() => onAnswer(false)} disabled={busy} aria-keyshortcuts="1"><RotateCcw /> 没想起 <ShortcutHint keys={["1"]} /></Button>
          <Button variant="primary" size="lg" onClick={() => onAnswer(true)} disabled={busy} aria-keyshortcuts="2"><Check /> 想起来了 <ShortcutHint keys={["2"]} /></Button>
        </> : null}
      </div>
    </section>
  );
}

function SpellingStage({ prompt, busy, onSubmit }: { prompt: SpellingPrompt; busy: boolean; onSubmit: (answer: string) => void }) {
  const [answer, setAnswer] = useState("");
  return (
    <section className="study-prompt study-prompt--spelling" aria-labelledby="spelling-title">
      <div className="study-stage-label"><PenLine /> 拼写巩固</div>
      <div className="study-spelling-meaning" id="spelling-title"><WordSenses senses={prompt.senses} empty="根据刚才学习的词完成拼写。" /></div>
      <form className="study-spelling-form" onSubmit={(event) => { event.preventDefault(); if (answer.trim()) onSubmit(answer); }}>
        <input autoFocus autoComplete="off" spellCheck={false} value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="输入英文单词" />
        <Button className="study-spelling-submit" type="submit" variant="primary" disabled={busy || !answer.trim()} aria-keyshortcuts="Enter"><Check /> 确认拼写 <ShortcutHint keys={["Enter"]} /></Button>
      </form>
    </section>
  );
}

function ExpectedWord({ expected, answer }: { expected: string; answer: string }) {
  const mismatches = spellingMismatchIndexes(answer, expected);
  return <strong className="spelling-diff" aria-label={`正确拼写 ${expected}`}>{[...expected].map((character, index) => <span key={`${character}-${index}`} className={mismatches.has(index) ? "mismatch" : ""}>{character}</span>)}</strong>;
}

function FeedbackStage({ result, submittedAnswer, onContinue }: { result: AttemptResult; submittedAnswer: string; onContinue: () => void }) {
  const spellingError = result.exercise === "spelling" && !result.correct && result.expectedHeadword;
  useStageKeyboard("feedback", (command) => { if (command === "advance") onContinue(); });
  return (
    <section className={result.correct ? "study-feedback study-feedback--correct" : "study-feedback study-feedback--retry"} role="status">
      <div className="study-feedback__mark">{result.correct ? <Check /> : <RotateCcw />}</div>
      <p className="section-label">{result.correct ? "本次通过" : "稍后再练一次"}</p>
      <h1>{result.exercise === "recall" ? (result.correct ? "词义回忆正确" : "先把词义重新连起来") : result.correct ? "拼写正确" : "留意不同的字母"}</h1>
      {result.word ? <div className="study-feedback__word"><strong>{result.word.headword}</strong><WordPronunciation wordId={result.word.wordId} headword={result.word.headword} phoneticUk={result.word.phoneticUk} phoneticUs={result.word.phoneticUs} /></div> : null}
      {result.answerSenses.length ? <div className="study-feedback__answer"><WordSenses senses={result.answerSenses} /></div> : null}
      {spellingError ? <div className="study-feedback__spelling"><span>正确拼写</span><ExpectedWord expected={spellingError} answer={submittedAnswer} /><small>你写的是 {submittedAnswer}</small></div> : null}
      <p>{result.correct ? "系统会继续安排下一阶段或下一词。" : "这个练习已重新排到队尾。"}</p>
      <Button variant="primary" size="lg" onClick={onContinue} aria-keyshortcuts="Enter Space">继续 <Play /> <ShortcutHint keys={["Enter", "Space"]} /></Button>
    </section>
  );
}

function CompletionStage({ session, starting, onContinue, onReset }: {
  session: SessionSnapshot;
  starting: boolean;
  onContinue: () => void;
  onReset: () => void;
}) {
  const ended = session.status === "ended";
  useStageKeyboard("completion", (command) => {
    if (command === "advance") ended ? onReset() : onContinue();
  }, false, starting);
  return (
    <main className="completion-page">
      <CelebrationConfetti />
      <section className="completion-celebration">
        <div className="completion-mark"><Check /></div>
        <p className="section-label">{ended ? "任务已结束" : "本组完成"}</p>
        <h1>{ended ? `完成了 ${session.completedCount} 个词` : `这一组 ${session.completedCount} 个词已完成`}</h1>
        <p className="completion-message">{ended
          ? `还有 ${session.unfinishedCount} 个词未完成；已开始的新词会在下次新词任务中优先出现。`
          : "学习结果已经写入今天的记忆计划，可以继续下一组。"}</p>
        <div className="completion-metrics">
          <div><strong>{session.completedCount}</strong><span>完成词数</span></div>
          <div><strong>{session.attemptCount}</strong><span>学习次数</span></div>
          <div><strong>{session.errorCount}</strong><span>本组纠错</span></div>
          <div><strong>{session.unfinishedCount}</strong><span>未完成词</span></div>
        </div>
        <div className="review-setup__action-buttons">
          {!ended ? <Button variant="primary" size="lg" onClick={onContinue} disabled={starting} aria-keyshortcuts="Enter Space"><Play /> {starting ? "正在准备…" : "继续下一组"} <ShortcutHint keys={["Enter", "Space"]} /></Button> : null}
          <Button variant={ended ? "primary" : "secondary"} size="lg" onClick={onReset} aria-keyshortcuts={ended ? "Enter Space" : undefined}>返回背诵计划 {ended ? <ShortcutHint keys={["Enter", "Space"]} /> : null}</Button>
        </div>
      </section>
    </main>
  );
}

function StudySetupPanel({
  setup,
  selectedSources,
  spellingEnabled,
  newTaskSize,
  reviewTaskSize,
  newWordOrder,
  availability,
  mode,
  reviewScope,
  activeSession,
  starting,
  ending,
  error,
  preferencesSaveState,
  onModeChange,
  onToggleSource,
  onSpellingChange,
  onNewWordOrderChange,
  onTaskSizeChange,
  onStart,
  onEnd,
}: {
  setup: StudySetupResponse;
  selectedSources: StudySource[];
  spellingEnabled: boolean;
  newTaskSize: number;
  reviewTaskSize: number;
  newWordOrder: NewWordOrder;
  availability: StudyAvailability;
  mode: StudyMode;
  reviewScope: ReviewScope;
  activeSession: SessionSnapshot | null;
  starting: boolean;
  ending: boolean;
  error: string;
  preferencesSaveState: "idle" | "saving" | "saved" | "error";
  onModeChange: (mode: StudyMode) => void;
  onToggleSource: (source: StudySource, selected: boolean) => void;
  onSpellingChange: (enabled: boolean) => void;
  onNewWordOrderChange: (order: NewWordOrder) => void;
  onTaskSizeChange: (mode: StudyMode, size: number) => void;
  onStart: () => void;
  onEnd: () => void;
}) {
  if (activeSession?.status === "active") {
    const label = activeSession.mode === "new_words" ? "新词学习" : activeSession.reviewScope === "mistakes" ? "专项错词复习" : "今日复习";
    return (
      <div className="page review-setup">
        <PageHeader title={`继续${label}`} description="当前任务已保存，可以从上次的位置继续。" />
        <section className="active-session-summary"><div><strong>{label}</strong><span>已完成 {activeSession.completedCount} 个，还有 {activeSession.remainingCount} 个正在队列中</span></div></section>
        {error ? <p className="error-strip" role="alert">{error}</p> : null}
        <div className="review-setup__actions">
          <div><span>当前任务</span><strong>{activeSession.completedCount} / {activeSession.targetCount} 个词</strong></div>
          <div className="review-setup__action-buttons">
            <Button variant="primary" size="lg" onClick={onStart} disabled={starting || ending}><Play /> 继续当前任务</Button>
            <Button variant="secondary" size="lg" onClick={onEnd} disabled={starting || ending}>{ending ? "正在结束…" : "结束当前任务"}</Button>
          </div>
        </div>
      </div>
    );
  }

  const isReview = mode === "review";
  const isMistakes = isReview && reviewScope === "mistakes";
  const taskSize = isReview ? reviewTaskSize : newTaskSize;
  const availableCount = isMistakes
    ? availability.mistakeReviewCount
    : isReview ? availability.reviewCount : availability.unfinishedNewCount + availability.newCount;
  const actualCount = Math.min(taskSize, availableCount);
  return (
    <div className="page review-setup">
      <PageHeader
        title={isMistakes ? "专项错词复习" : "背诵计划"}
        description={isMistakes ? "集中处理近期答错的词，并继续更新长期复习排期。" : "新词与到期复习分开成组，每组完成后可以继续下一组。"}
        action={<Link className="button button--secondary button--md" to="/app/$subject/practice" params={{ subject: "english" }}><Dumbbell /> 自由练习</Link>}
      />
      {!isMistakes ? (
        <section className="study-mode-switch" aria-label="背诵模式">
          <button type="button" className={mode === "new_words" ? "active" : ""} onClick={() => onModeChange("new_words")}><BookOpen /> 学习新词</button>
          <button type="button" className={mode === "review" ? "active" : ""} onClick={() => onModeChange("review")}><RotateCcw /> 今日复习</button>
        </section>
      ) : null}

      {!isMistakes ? (
        <section className="book-selection" aria-labelledby="study-source-title">
          <div className="book-selection__heading"><h2 id="study-source-title">选择学习范围</h2><span>已选择 {selectedSources.length} 组</span></div>
          <div className="book-options">
            {setup.sources.map((source) => {
              const selected = selectedSources.includes(source.id);
              return (
                <label key={source.id} className={selected ? "book-option book-option--selected" : "book-option"}>
                  <input type="checkbox" checked={selected} onChange={(event) => onToggleSource(source.id, event.target.checked)} />
                  <span className="book-option__check" aria-hidden="true">{selected ? <Check /> : null}</span>
                  <span className="book-option__copy"><strong>{source.label}</strong><small>{SOURCE_DESCRIPTIONS[source.id]}</small></span>
                  <span className="book-option__count"><strong>{source.count.toLocaleString()}</strong><small>个词</small></span>
                </label>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="mistake-practice-summary"><RotateCcw /><div><strong>{availability.mistakeReviewCount} 个可专项复习错词</strong><span>当天已经结算过的词不会重复进入任务</span></div></section>
      )}

      <section className="review-parameters" aria-labelledby="study-settings-title">
        <div className="review-parameters__heading"><h2 id="study-settings-title">本组设置</h2><span>{preferencesSaveState === "saving" ? "正在保存设置…" : preferencesSaveState === "error" ? "设置保存失败" : preferencesSaveState === "saved" ? "设置已保存" : "词数会按模式分别记住"}</span></div>
        <div className="review-parameter-row">
          <div><strong>本组词数</strong><small>当前可用 {availableCount} 个，本组实际进入 {actualCount} 个</small></div>
          <div className="word-count-stepper">
            <button type="button" onClick={() => onTaskSizeChange(mode, taskSize - 1)} disabled={taskSize <= STUDY_TARGET_MIN} aria-label="减少词数"><Minus /></button>
            <input type="number" min={STUDY_TARGET_MIN} max={STUDY_TARGET_MAX} value={taskSize} onChange={(event) => onTaskSizeChange(mode, Number(event.target.value))} aria-label="本组词数" />
            <button type="button" onClick={() => onTaskSizeChange(mode, taskSize + 1)} disabled={taskSize >= STUDY_TARGET_MAX} aria-label="增加词数"><Plus /></button>
          </div>
        </div>
        {!isReview ? (
          <div className="review-parameter-row">
            <div><strong>新词顺序</strong><small>未完成的新词始终优先</small></div>
            <div className="review-order-control" role="radiogroup" aria-label="新词顺序">
              <button type="button" role="radio" aria-checked={newWordOrder === "recent"} className={newWordOrder === "recent" ? "active" : ""} onClick={() => onNewWordOrderChange("recent")}><Clock3 /> 最近添加</button>
              <button type="button" role="radio" aria-checked={newWordOrder === "alphabetical"} className={newWordOrder === "alphabetical" ? "active" : ""} onClick={() => onNewWordOrderChange("alphabetical")}><ArrowDownAZ /> A-Z</button>
            </div>
          </div>
        ) : null}
        <div className="review-parameter-row">
          <div><strong>拼写训练</strong><small>想起词义后追加中译英；仅拼写失败会记为较难</small></div>
          <label className="study-toggle"><input type="checkbox" checked={spellingEnabled} onChange={(event) => onSpellingChange(event.target.checked)} /><span aria-hidden="true" /><strong>{spellingEnabled ? "已开启" : "已关闭"}</strong></label>
        </div>
      </section>
      {error ? <p className="error-strip" role="alert">{error}</p> : null}
      <div className="review-setup__actions">
        <div><span>本组预计</span><strong>{!isReview ? `先引入 ${Math.min(STUDY_INITIAL_BATCH_SIZE, actualCount)} 个，共完成 ${actualCount} 个` : `${actualCount} 个词`}</strong></div>
        <Button variant="primary" size="lg" onClick={onStart} disabled={starting || !actualCount || (!isMistakes && !selectedSources.length)}><Play /> {starting ? "正在准备…" : isReview ? "开始复习" : "开始学习"}</Button>
      </div>
    </div>
  );
}

function optimisticIntroductionSession(session: SessionSnapshot, prompt: IntroductionPrompt): SessionSnapshot {
  if (session.prefetchedCard) return { ...session, attemptCount: session.attemptCount + 1, card: session.prefetchedCard, prefetchedCard: null };
  return {
    ...session,
    attemptCount: session.attemptCount + 1,
    card: {
      itemId: prompt.itemId,
      wordId: prompt.wordId,
      exercise: "recall",
      headword: prompt.headword,
      phoneticUk: prompt.phoneticUk,
      phoneticUs: prompt.phoneticUs,
      senses: prompt.senses,
    },
    prefetchedCard: null,
  };
}

export function ReviewPage() {
  const queryClient = useQueryClient();
  const initialized = useRef(false);
  const answerStartedAt = useRef(Date.now());
  const attemptPending = useRef(false);
  const pendingItemId = useRef<string | null>(null);
  const queuedAttempt = useRef<(() => void) | null>(null);
  const search = new URLSearchParams(window.location.search);
  const reviewScope: ReviewScope = search.get("scope") === "mistakes" ? "mistakes" : "regular";
  const [mode, setMode] = useState<StudyMode>(reviewScope === "mistakes" || search.get("mode") === "review" ? "review" : "new_words");
  const [selectedSources, setSelectedSources] = useState<StudySource[]>(() => [...DEFAULT_STUDY_PREFERENCES.sources]);
  const [spellingEnabled, setSpellingEnabled] = useState(DEFAULT_STUDY_PREFERENCES.spellingEnabled);
  const [newTaskSize, setNewTaskSize] = useState(DEFAULT_STUDY_PREFERENCES.newTaskSize);
  const [reviewTaskSize, setReviewTaskSize] = useState(DEFAULT_STUDY_PREFERENCES.reviewTaskSize);
  const [newWordOrder, setNewWordOrder] = useState<NewWordOrder>(DEFAULT_STUDY_PREFERENCES.newWordOrder);
  const [session, setSession] = useState<SessionSnapshot | null>(null);
  const [feedback, setFeedback] = useState<AttemptResult | null>(null);
  const [submittedAnswer, setSubmittedAnswer] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState("");
  const [preferencesSaveState, setPreferencesSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const setupQuery = useQuery({ queryKey: ["study", "setup"], queryFn: () => apiFetch<StudySetupResponse>("/study/setup") });
  const activeQuery = useQuery({
    queryKey: ["study", "active-session"],
    queryFn: async () => (await apiFetch<{ session: SessionSnapshot | null }>("/study/sessions/active")).session,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (activeQuery.data && !showSetup) {
      setSession((current) => current ?? activeQuery.data);
      setMode(activeQuery.data.mode);
    }
  }, [activeQuery.data, showSetup]);
  useEffect(() => {
    const preferences = setupQuery.data?.preferences;
    if (!preferences || initialized.current || activeQuery.isLoading || activeQuery.data) return;
    initialized.current = true;
    setSelectedSources(preferences.sources);
    setSpellingEnabled(preferences.spellingEnabled);
    setNewTaskSize(preferences.newTaskSize);
    setReviewTaskSize(preferences.reviewTaskSize);
    setNewWordOrder(preferences.newWordOrder);
  }, [activeQuery.data, activeQuery.isLoading, setupQuery.data]);

  const sourceKey = [...selectedSources].sort().join(",");
  const availabilityQuery = useQuery({
    queryKey: ["study", "availability", sourceKey],
    queryFn: () => apiFetch<StudyAvailability>(`/study/preview?sources=${selectedSources.map(encodeURIComponent).join(",")}`),
    enabled: Boolean(setupQuery.data && selectedSources.length),
  });
  const availability = availabilityQuery.data ?? setupQuery.data?.availability ?? EMPTY_AVAILABILITY;
  const setupReady = Boolean(setupQuery.data);

  useEffect(() => {
    if (!initialized.current || !setupReady || !selectedSources.length) return;
    setPreferencesSaveState("saving");
    const timer = window.setTimeout(() => {
      void apiFetch<{ preferences: StudyPreferences }>("/study/preferences", {
        method: "PUT",
        body: JSON.stringify({
          sources: selectedSources,
          spellingEnabled,
          newTaskSize,
          reviewTaskSize,
          newWordOrder,
        }),
      }).then(async ({ preferences }) => {
        queryClient.setQueryData<StudySetupResponse>(["study", "setup"], (current) => current ? { ...current, preferences } : current);
        await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        setPreferencesSaveState("saved");
      }).catch(() => setPreferencesSaveState("error"));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [newTaskSize, newWordOrder, queryClient, reviewTaskSize, selectedSources, setupReady, spellingEnabled]);

  useEffect(() => {
    answerStartedAt.current = Date.now();
    setError("");
  }, [session?.card?.itemId, session?.card?.exercise]);

  const createSession = async () => {
    setStarting(true);
    setError("");
    try {
      const taskSize = mode === "new_words" ? newTaskSize : reviewTaskSize;
      const next = await apiFetch<SessionSnapshot>("/study/sessions", {
        method: "POST",
        body: JSON.stringify(mode === "new_words"
          ? { mode, sources: selectedSources, spellingEnabled, taskSize, newWordOrder }
          : { mode, reviewScope, sources: selectedSources, spellingEnabled, taskSize }),
      });
      setSession(next);
      setFeedback(null);
      setShowSetup(false);
      queryClient.setQueryData(["study", "active-session"], next.status === "active" ? next : null);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["study", "setup"] }),
        queryClient.invalidateQueries({ queryKey: ["study", "availability"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    } catch (cause) {
      if (cause instanceof Error && cause.message === "active_session_exists") {
        const existing = (await activeQuery.refetch()).data;
        if (existing) {
          setSession(existing);
          setMode(existing.mode);
          setShowSetup(false);
          return;
        }
      }
      setError("无法创建本组任务，请检查学习范围后重试。");
    } finally {
      setStarting(false);
    }
  };

  const resumeOrStart = () => {
    const active = queryClient.getQueryData<SessionSnapshot | null>(["study", "active-session"]);
    if (active?.status === "active") {
      setSession(active);
      setShowSetup(false);
      return;
    }
    void createSession();
  };

  const endSession = async () => {
    const active = queryClient.getQueryData<SessionSnapshot | null>(["study", "active-session"]);
    if (!active || !window.confirm("结束当前任务？已开始的新词会保留当前阶段，未开始的词会释放回词池。")) return;
    setEnding(true);
    try {
      const ended = await apiFetch<SessionSnapshot>(`/study/sessions/${active.sessionId}/end`, { method: "POST" });
      queryClient.setQueryData(["study", "active-session"], null);
      setSession(ended);
      setShowSetup(false);
      void queryClient.invalidateQueries({ queryKey: ["study"] });
    } catch {
      setError("结束任务失败，当前进度仍然保留。");
    } finally {
      setEnding(false);
    }
  };

  const submitAttempt = async (payload: Record<string, unknown>, answer = "") => {
    const card = session?.card;
    if (!session || !card) return;
    if (attemptPending.current) {
      if (pendingItemId.current === card.itemId) return;
      queuedAttempt.current = () => { void submitAttempt(payload, answer); };
      return;
    }
    const previous = session;
    const optimistic = card.exercise === "introduction" ? optimisticIntroductionSession(session, card) : null;
    attemptPending.current = true;
    pendingItemId.current = card.itemId;
    let saved = false;
    if (card.exercise !== "introduction") setBusy(true);
    if (optimistic) {
      setSession(optimistic);
      queryClient.setQueryData(["study", "active-session"], optimistic);
    }
    try {
      const result = await apiFetch<AttemptResult>(`/study/sessions/${session.sessionId}/attempts`, {
        method: "POST",
        body: JSON.stringify({
          sessionItemId: card.itemId,
          exercise: card.exercise,
          responseMs: Math.max(0, Date.now() - answerStartedAt.current),
          ...payload,
        }),
      });
      queryClient.setQueryData(["study", "active-session"], result.session.status === "active" ? result.session : null);
      if (card.exercise === "introduction") {
        setSession(result.session);
      } else {
        setSubmittedAnswer(answer);
        setFeedback(result);
      }
      saved = true;
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["user-words"] }),
        queryClient.invalidateQueries({ queryKey: ["word-collection"] }),
      ]);
    } catch {
      queuedAttempt.current = null;
      if (optimistic) {
        setSession(previous);
        queryClient.setQueryData(["study", "active-session"], previous);
      }
      setError("本次结果没有保存，请重试。");
    } finally {
      attemptPending.current = false;
      pendingItemId.current = null;
      setBusy(false);
      const queued = saved ? queuedAttempt.current : null;
      queuedAttempt.current = null;
      if (queued) queueMicrotask(queued);
    }
  };

  const continueFromFeedback = () => {
    if (!feedback) return;
    setSession(feedback.session);
    queryClient.setQueryData(["study", "active-session"], feedback.session.status === "active" ? feedback.session : null);
    setFeedback(null);
    setSubmittedAnswer("");
  };
  const resetSession = () => {
    queuedAttempt.current = null;
    queryClient.setQueryData(["study", "active-session"], null);
    setSession(null);
    setFeedback(null);
    setShowSetup(true);
    initialized.current = false;
    void queryClient.invalidateQueries({ queryKey: ["study"] });
  };
  const exitToSetup = useCallback(() => {
    queuedAttempt.current = null;
    setSession(null);
    setFeedback(null);
    setShowSetup(true);
  }, []);
  useEffect(() => {
    if (session?.status !== "active" || showSetup) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      exitToSetup();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [exitToSetup, session?.status, showSetup]);

  if (setupQuery.isLoading || activeQuery.isLoading) return <div className="page"><LoadingLine /></div>;
  if (!setupQuery.data) return <div className="page"><EmptyState title="背诵计划暂时无法加载" description="请刷新页面后重试。" /></div>;
  if (!session || showSetup) {
    const active = queryClient.getQueryData<SessionSnapshot | null>(["study", "active-session"]);
    return <StudySetupPanel
      setup={setupQuery.data}
      selectedSources={selectedSources}
      spellingEnabled={spellingEnabled}
      newTaskSize={newTaskSize}
      reviewTaskSize={reviewTaskSize}
      newWordOrder={newWordOrder}
      availability={availability}
      mode={mode}
      reviewScope={reviewScope}
      activeSession={active ?? null}
      starting={starting}
      ending={ending}
      error={error}
      preferencesSaveState={preferencesSaveState}
      onModeChange={setMode}
      onToggleSource={(source, selected) => setSelectedSources((current) => selected ? [...new Set([...current, source])] : current.filter((item) => item !== source))}
      onSpellingChange={setSpellingEnabled}
      onNewWordOrderChange={setNewWordOrder}
      onTaskSizeChange={(targetMode, value) => {
        const size = Math.min(STUDY_TARGET_MAX, Math.max(STUDY_TARGET_MIN, Number.isFinite(value) ? Math.trunc(value) : STUDY_TARGET_MIN));
        if (targetMode === "new_words") setNewTaskSize(size); else setReviewTaskSize(size);
      }}
      onStart={resumeOrStart}
      onEnd={() => void endSession()}
    />;
  }
  if (session.status === "completed" || session.status === "ended") return <CompletionStage session={session} starting={starting} onContinue={() => void createSession()} onReset={resetSession} />;
  if (session.status === "exhausted") return <div className="page review-setup"><EmptyState title="当前没有可练单词" description="完成其他任务，或调整学习范围后再试。" action={<Button variant="primary" onClick={resetSession}>返回背诵计划</Button>} /></div>;

  const progress = session.targetCount ? Math.min(100, session.completedCount / session.targetCount * 100) : 0;
  const prompt = session.card;
  return (
    <div className="review-page review-page--staged">
      <header className="review-toolbar">
        <button type="button" className="study-exit" onClick={exitToSetup} aria-keyshortcuts="Escape"><LogOut /> 退出 <ShortcutHint keys={["Esc"]} /></button>
        <div className="review-progress"><div><span style={{ width: `${progress}%` }} /></div><strong>{session.completedCount} / {session.targetCount}</strong></div>
        <div className="review-session-kind">{session.mode === "new_words" ? <><BookOpen /> 学习新词</> : session.reviewScope === "mistakes" ? <><RotateCcw /> 专项错词</> : <><RotateCcw /> 今日复习</>}</div>
      </header>
      <main className="review-stage review-stage--staged" aria-busy={busy}>
        <div className="study-stage-frame" key={feedback ? `feedback-${prompt?.itemId}` : `${prompt?.itemId}-${prompt?.exercise}`}>
          {feedback ? <FeedbackStage result={feedback} submittedAnswer={submittedAnswer} onContinue={continueFromFeedback} /> : null}
          {!feedback && prompt?.exercise === "introduction" ? <IntroductionStage prompt={prompt} onContinue={() => void submitAttempt({ acknowledged: true })} /> : null}
          {!feedback && prompt?.exercise === "recall" ? <RecallStage prompt={prompt} busy={busy} onAnswer={(remembered) => void submitAttempt({ remembered })} /> : null}
          {!feedback && prompt?.exercise === "spelling" ? <SpellingStage prompt={prompt} busy={busy} onSubmit={(answer) => void submitAttempt({ answer }, answer)} /> : null}
          {!feedback && !prompt ? <EmptyState title="正在取得下一项练习" description="当前进度已保存。" action={<Button onClick={() => void apiFetch<SessionSnapshot>(`/study/sessions/${session.sessionId}/next`, { method: "POST" }).then(setSession)}>继续</Button>} /> : null}
        </div>
      </main>
      <footer className="review-footer"><span>完成 {session.completedCount} 个词 · 尝试 {session.attemptCount} 次 · 纠错 {session.errorCount} 次</span><span>{session.mode === "new_words" ? `已加入 ${session.introducedCount} 个 · 队列 ${session.activeCount} 个` : `剩余 ${session.remainingCount} 个`}</span></footer>
      {error ? <div className="error-strip review-error" role="alert">{error}</div> : null}
    </div>
  );
}
